import { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { useAuth } from '../../context/useAuth'
import { firestoreDb, isDebugMode } from '../../services/firebase'
import { resolveComputedDisplayName } from '../../utils/userIdentity'
import { Pressable } from '../Common/Pressable'

export const ProfileDebugOverlay = () => {
  const { currentUser, userProfile } = useAuth()
  const [raw, setRaw] = useState<Record<string, unknown> | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!currentUser?.uid) {
      setRaw(null)
      return
    }
    return onSnapshot(doc(firestoreDb, 'users', currentUser.uid), (snapshot) => {
      setRaw(snapshot.exists() ? snapshot.data() : null)
    }, () => setRaw(null))
  }, [currentUser?.uid])

  if (!isDebugMode || !currentUser) return null

  const role = userProfile?.role ?? raw?.role ?? 'MEMBER'
  const computedName = resolveComputedDisplayName(userProfile ?? raw)
  const emailHidden = role === 'HELPER'

  return (
    <aside
      style={{
        position: 'fixed',
        right: open ? 92 : 96,
        top: 14,
        zIndex: 100000,
        width: open ? 360 : 44,
        maxWidth: 'calc(100vw - 112px)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-ctl-sm, 8px)',
        background: 'var(--surface-raised)',
        color: 'var(--text)',
        boxShadow: 'var(--shadow-pop)',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        fontSize: 12,
        overflow: 'hidden',
      }}
      data-testid="profile-debug-overlay"
    >
      <Pressable
        onClick={() => setOpen((current) => !current)}
        style={{
          width: '100%',
          border: 0,
          background: 'var(--surface-muted)',
          color: 'var(--text-strong)',
          padding: open ? '8px 10px' : '8px 0',
          textAlign: open ? 'left' : 'center',
          cursor: 'pointer',
          fontWeight: 700,
          minHeight: 36,
        }}
        aria-label="Profile debug"
      >
        {open ? 'Profile Debug' : 'DBG'}
      </Pressable>
      {open && (
        <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div><strong>computedName:</strong> {computedName}</div>
          <div><strong>role:</strong> {String(role)}</div>
          <div><strong>Email Field Hidden:</strong> {String(emailHidden).toUpperCase()}</div>
          <pre style={{ maxHeight: 240, overflow: 'auto', margin: 0, whiteSpace: 'pre-wrap', color: 'var(--muted)' }}>
            {JSON.stringify(raw ?? userProfile, null, 2)}
          </pre>
        </div>
      )}
    </aside>
  )
}
